import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePWAInstall } from "@/context/PWAInstallContext";

/**
 * Auto-prompt bottom sheet for mobile users on first sign-in.
 * Behaviour:
 *  - Only shows on narrow viewports (< 768px)
 *  - Only when a beforeinstallprompt event has fired (canInstall)
 *  - Hides forever (30 days) once user dismisses
 *  - iOS Safari: shows a manual instruction sheet (no beforeinstallprompt on iOS)
 */
export default function PWAInstallPrompt() {
  const { t } = useTranslation();
  const { canInstall, installed, promptInstall, markDismissed, isRecentlyDismissed } = usePWAInstall();
  const [open, setOpen] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (installed) return;
    if (isRecentlyDismissed && isRecentlyDismissed()) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;

    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

    if (canInstall) {
      const t1 = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t1);
    }
    if (isIOS) {
      const t1 = setTimeout(() => { setIosMode(true); setOpen(true); }, 1500);
      return () => clearTimeout(t1);
    }
  }, [canInstall, installed, isRecentlyDismissed]);

  const close = () => {
    setOpen(false);
    markDismissed && markDismissed();
  };

  const doInstall = async () => {
    const ok = await promptInstall();
    setOpen(false);
    if (!ok) markDismissed && markDismissed();
  };

  if (!open || installed) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={close}
      data-testid="pwa-install-prompt"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl md:rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-900 text-white grid place-items-center">
            <Smartphone className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              {t("common.install_app")}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              {t("common.install_hint")}
            </div>
          </div>
          <button
            onClick={close}
            className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
            aria-label="close"
            data-testid="pwa-install-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {iosMode ? (
          <div className="mt-4 text-sm text-slate-600 dark:text-slate-300 space-y-2">
            <div>1. Appuyez sur l&apos;icône de partage (carré avec flèche) dans Safari.</div>
            <div>2. Choisissez «&nbsp;Sur l&apos;écran d&apos;accueil&nbsp;».</div>
            <div>3. Validez avec «&nbsp;Ajouter&nbsp;».</div>
          </div>
        ) : (
          <div className="mt-6 flex gap-2">
            <button
              onClick={close}
              className="flex-1 rounded-full border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 py-2.5 text-sm font-medium"
              data-testid="pwa-install-later"
            >
              {t("common.later")}
            </button>
            <button
              onClick={doInstall}
              className="flex-1 rounded-full bg-blue-900 hover:bg-blue-800 text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
              data-testid="pwa-install-accept"
            >
              <Download className="w-4 h-4" /> {t("common.install")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
